/**
 * useSidebarResize Hook
 *
 * Provides macOS Finder-style resizable sidebar functionality.
 * Handles drag interactions, width constraints, and localStorage persistence.
 */

import { useState, useCallback, useEffect, useRef } from 'react';

const SIDEBAR_WIDTH_KEY = 'modcar.sidebarWidth';
const DEFAULT_WIDTH = 240;
const MIN_WIDTH = 65;
const MAX_WIDTH = 400;

interface UseSidebarResizeReturn {
  width: number;
  isResizing: boolean;
  handleMouseDown: (e: React.PointerEvent) => void;
}

export function useSidebarResize(): UseSidebarResizeReturn {
  // Initialize width from localStorage to avoid layout shift
  const [width, setWidth] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_WIDTH;
    const stored = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) {
        return parsed;
      }
    }
    return DEFAULT_WIDTH;
  });

  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Persist width to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, String(width));
  }, [width]);

  const handleMouseDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;

      // Capture pointer for smooth dragging even outside the element
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [width]
  );

  useEffect(() => {
    if (!isResizing) return;

    const handlePointerMove = (e: PointerEvent) => {
      // Use requestAnimationFrame for smooth updates
      requestAnimationFrame(() => {
        const delta = e.clientX - startXRef.current;
        const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidthRef.current + delta));
        setWidth(newWidth);
      });
    };

    const handlePointerUp = () => {
      setIsResizing(false);
    };

    // Add listeners to document for reliable tracking
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);

    // Disable text selection while dragging
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing]);

  return {
    width,
    isResizing,
    handleMouseDown,
  };
}

export { MIN_WIDTH, MAX_WIDTH, DEFAULT_WIDTH };
